import React, { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Newspaper, RefreshCw } from "lucide-react";
import { getNews, getSources } from "../lib/api";
import { usePageContent, kickerStyle, titleStyle } from "../context/PageContentContext";
import SiteActionBar from "./SiteActionBar";

export const NewsSection = () => {
  const copy = usePageContent("news");
  const [sources, setSources] = useState([]);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    getSources().then(setSources).catch(() => setSources([]));
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getNews(active)
      .then((d) => {
        if (alive) setItems(d);
      })
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [active]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const d = await getNews(active);
      setItems(d);
    } finally {
      setRefreshing(false);
    }
  };

  const filters = useMemo(
    () => [{ id: "all", name: "All", color: "#0A0F17", short: "All" }, ...sources],
    [sources]
  );

  return (
    <section
      className="mx-auto max-w-7xl px-5 pt-4 pb-16 sm:px-8 sm:pt-10"
      data-testid="news-section"
    >
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        {copy.hide_header ? (
          <div />
        ) : (
          <div>
            <div className="flex items-center gap-2 text-brand-accent" style={kickerStyle(copy)}>
              <Newspaper size={16} strokeWidth={2.5} />
              <span className="label-kicker">{copy.kicker}</span>
            </div>
            <h2
              className="brand-headline mt-2 whitespace-pre-line font-display text-3xl font-black tracking-tight text-[var(--brand-ink)] sm:text-4xl"
              style={titleStyle(copy)}
            >
              {copy.title}
            </h2>
            {copy.intro ? (
              <p className="mt-2 max-w-xl text-sm text-[var(--brand-body)]">
                {copy.intro}
              </p>
            ) : null}
          </div>
        )}
        <div className="flex items-center gap-2 self-start sm:self-end">
          <SiteActionBar inline />
          <button
            onClick={refresh}
            disabled={refreshing}
            data-testid="news-refresh"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-black/10 bg-white text-[var(--brand-ink)] transition-colors hover:border-[#0A0F17]"
            aria-label="Refresh news"
          >
            <RefreshCw
              size={16}
              className={refreshing ? "animate-spin" : ""}
            />
          </button>
        </div>
      </div>

      {/* Filter pills */}
      <div
        className="mb-8 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-2 no-scrollbar"
        data-testid="news-filters"
      >
        {filters.map((s) => (
          <button
            key={s.id}
            onClick={() => setActive(s.id)}
            data-testid={`news-filter-${s.id}`}
            className={`snap-start whitespace-nowrap rounded-full border px-4 py-2 font-display text-sm font-semibold transition-colors ${
              active === s.id
                ? "border-[#0A0F17] bg-[#0A0F17] text-white"
                : "border-black/10 bg-white text-[var(--brand-body)] hover:border-[#0A0F17] hover:text-[var(--brand-ink)]"
            }`}
          >
            <span className="flex items-center gap-2">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: s.color }}
              />
              {s.name}
            </span>
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="news-loading">
          {["s1","s2","s3","s4","s5","s6"].map((id) => (
            <div
              key={id}
              className="animate-pulse rounded-md border border-black/10 bg-white p-0"
            >
              <div className="h-40 w-full bg-[#F1F5F9]" />
              <div className="space-y-2 p-5">
                <div className="h-3 w-1/4 bg-[#F1F5F9]" />
                <div className="h-4 w-full bg-[#F1F5F9]" />
                <div className="h-4 w-5/6 bg-[#F1F5F9]" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-md border border-dashed border-black/15 bg-white p-10 text-center"
          data-testid="news-empty"
        >
          <p className="font-display text-lg font-bold text-[var(--brand-ink)]">
            No stories right now.
          </p>
          <p className="mt-1 text-sm text-[var(--brand-body)]">
            Check back in a few minutes — the feed refreshes every 5.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="news-grid">
          {items.map((n, i) => (
            <NewsCard key={n.id} item={n} index={i} />
          ))}
        </div>
      )}
    </section>
  );
};

const NEWS_HEADER_COLOR = "#052962";

const NewsCard = ({ item, index }) => {
  return (
    <a
      href={item.link}
      target="_blank"
      rel="noreferrer"
      className="card-hover group relative flex flex-col overflow-hidden rounded-md border border-black/10 bg-white fade-in-up"
      style={{ animationDelay: `${Math.min(index, 10) * 40}ms` }}
      data-testid="news-card"
    >
      {/* Source colour header — uniform navy across every source so all
          card rows line up cleanly at the top of the news grid. */}
      <div
        className="flex items-center justify-between gap-3 px-5 py-3"
        style={{ backgroundColor: NEWS_HEADER_COLOR }}
      >
        <span className="label-kicker text-white">{item.source_name}</span>
        <span className="label-kicker text-white/80">
          {item.published_human}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-3 px-5 pb-5 pt-4">
        <h3 className="line-clamp-4 font-display text-xl font-bold leading-[1.2] tracking-tight text-[var(--brand-ink)] transition-colors group-hover:text-[#5A9AC9]">
          {item.title}
        </h3>
        {item.summary ? (
          <p className="line-clamp-3 text-sm leading-relaxed text-[var(--brand-body)]">
            {item.summary}
          </p>
        ) : null}
        <div className="mt-auto flex items-center justify-between border-t border-black/5 pt-3 text-xs text-[var(--brand-body)]">
          <span className="label-kicker">Read on {item.source_name}</span>
          <ArrowUpRight
            size={16}
            className="text-[var(--brand-ink)] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
          />
        </div>
      </div>
    </a>
  );
};
